import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 rounded-full",
  {
    variants: {
      variant: {
        // Primary action: Dark background
        default: "bg-foreground text-background hover:bg-foreground/90",
        // Destructive
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        // Outlined: Subtle border
        outline: "border border-border bg-transparent hover:bg-muted",
        // Secondary: Muted
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        // Ghost: Text only with hover
        ghost: "hover:bg-muted hover:text-foreground",
        // Link style
        link: "text-foreground underline-offset-4 hover:underline",
        // Inverted: For dark backgrounds
        inverted: "bg-background text-foreground hover:bg-background/90",
        // Primary yellow
        primary: "bg-primary text-primary-foreground hover:bg-primary/90",
      },
      size: {
        default: "h-11 px-6 text-sm",
        sm: "h-9 px-5 text-xs",
        lg: "h-12 px-8 text-base",
        xl: "h-14 px-10 text-lg",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
